import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
// Utilities
import { useSnackbar } from '../../utils/useSnackbar';
import { ResetPasswordSchema } from '../../Validation/ValidationRule';
import { resetPassword } from '../../utils/api/user_apis';
import { STATUS } from '../../utils/api/sharedfunction';
// components
import FormWrapper from '../../components/Forms/FormWrapper';
import { PasswordField } from '../../components/PasswordField';
import LegalConsentCheckbox from '../../components/consent/LegalConsentCheckbox';
import { CONSENT_COPY } from '../../constants/consentCopy';

const PasswordAction = ({ type = 'reset' }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  // Dynamic configuration based on type
  const isSetPassword = type === 'set';
  const config = {
    title: isSetPassword ? "Set Password" : "Reset Password",
    buttonText: isSetPassword ? "Set Password" : "Reset Password",
    successMessage: isSetPassword ? "Password set successfully!" : "Password reset successfully!"
  };

  // State variable to store token
  const [token, setToken] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [consentMeta, setConsentMeta] = useState({ ready: false });
  const [consentError, setConsentError] = useState(null);

  const handleConsentChange = useCallback((meta) => {
    setConsentAgreed(meta.agreed);
    setConsentMeta(meta);
    if (meta.agreed) {
      setConsentError(null);
    }
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    // Parse URL parameters using URLSearchParams
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');
    // intentionally ignore any 'email' query param; user is resolved from token server-side

    if (!tokenParam) {
      setMessage("Invalid reset link");
      setMessageType("error");
      return;
    }

    setToken(tokenParam);
  }, [location]);

  const onSubmit = async (data) => {
    if (!consentAgreed) {
      setConsentError(CONSENT_COPY.accountValidationError);
      return;
    }
    if (!consentMeta.ready) {
      setConsentError(consentMeta.error || 'Legal documents are not available. Please try again later.');
      return;
    }

    try {
      const response = await resetPassword({
        token: token,
        newPassword: data.newPassword,
        tosLegalDocumentVersionId: consentMeta.tosLegalDocumentVersionId,
        privacyLegalDocumentVersionId: consentMeta.privacyLegalDocumentVersionId,
      });

      if (response.status === STATUS.SUCCESS) {
        showSnackbar(config.successMessage, 'success');
        reset();
        setTimeout(() => {
          navigate("/login"); 
        }, 1500);
      } else {
        showSnackbar(response.error || 'Failed to reset password. Please try again.', 'error');
      }
    } catch (error) {
      console.error(error);
      showSnackbar(error.error || 'Failed to reset password. Please try again.', 'error');
    }
  };

  return (
    <Box>
      <FormWrapper
        title={config.title}
        onSubmit={handleSubmit(onSubmit)}
        loading={false}
        error={message}
        buttonName={config.buttonText}
        submitDisabled={!isValid || !consentAgreed || !consentMeta.ready}
      >
        {/* Display error message if there is an invalid link */}
        {message && (
          <div style={{ color: messageType === 'error' ? 'red' : 'green' }}>
            {message}
          </div>
        )}
        
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              field={field}
              label="New Password"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              field={field}
              label="Confirm Password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />
          )}
        />
        <Box sx={{ mt: 2 }}>
          <LegalConsentCheckbox
            checked={consentAgreed}
            onChange={handleConsentChange}
            error={consentError}
            id="set-password-consent"
          />
        </Box>
      </FormWrapper>
    </Box>
  );
};

export default PasswordAction;
